import { AlertCircle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { validateCsvFile } from '@/api/file-upload-api';
import { useFileUpload } from '@/hooks/use-file-upload';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { PreviewStage } from './preview-stage';
import type { CsvValidationResponse, ModalStage, PaymentFileUploadModalProps } from './types';
import { UploadStage } from './upload-stage';

export function PaymentFileUploadModal({
  open,
  onClose,
  onUploadSuccess,
  partyId,
  actingOnBehalfClientName,
}: PaymentFileUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [validationResponse, setValidationResponse] = useState<CsvValidationResponse | null>(null);
  const [stage, setStage] = useState<ModalStage>('upload');
  const [submitNotice, setSubmitNotice] = useState<{
    kind: 'upload_failed' | 'processing_failed';
    message: string;
    fileId: string | null;
  } | null>(null);
  // The file already stored by a processing failure. Submit stays off until a different file is chosen.
  const [storedFile, setStoredFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use the file upload hook for upload + automatic processing
  const { upload, isUploading, isProcessing, reset: resetUpload } = useFileUpload();
  const isSubmitting = isUploading || isProcessing;

  // Derived state
  const isSuccess = validationResponse?.success === true;
  const validationResult = validationResponse
    ? isSuccess
      ? validationResponse.data.validation_result
      : validationResponse.validation_result
    : undefined;
  const previewRows = validationResponse
    ? isSuccess
      ? validationResponse.data.preview_rows || []
      : validationResponse.preview_rows || []
    : [];
  const columnHeaders = previewRows.length > 0 ? Object.keys(previewRows[0].original_data) : [];

  // Handlers
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setValidationResponse(null);
      setSubmitNotice(null);
      setStoredFile(null);
      setStage('upload');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setValidationResponse(null);
      setSubmitNotice(null);
      setStoredFile(null);
      setStage('upload');
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      toast.error('No file selected', {
        description: 'Please select a file to validate',
      });
      return;
    }

    setIsValidating(true);
    setError(null);
    setSubmitNotice(null);

    try {
      const response = await validateCsvFile({
        file: selectedFile,
        party_id: partyId,
        file_type_id: 1,
        ...(description ? { description } : {}),
      });

      setValidationResponse(response);
      setStage('preview');

      if (response.success) {
        toast.success('Validation successful', {
          description: `${response.data.validation_result.valid_rows} of ${response.data.validation_result.total_rows} rows are valid.`,
        });
      } else {
        toast.error('Validation failed', {
          description: response.error || 'Please check the validation results.',
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Validation failed';
      setError(new Error(errorMessage));
      toast.error('Validation error', {
        description: errorMessage,
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (!validationResponse?.success || !selectedFile || storedFile === selectedFile) return;

    setSubmitNotice(null);

    try {
      toast.info('Uploading file...', { id: 'upload-progress' });

      const result = await upload({
        file: selectedFile,
        party_id: partyId,
        file_type_id: 1,
        ...(description ? { description } : {}),
      });

      if (result.status === 'processed' && result.response) {
        toast.success('Payment file uploaded and processed successfully', {
          id: 'upload-progress',
          description: `File has been processed with ${result.response.processing_result?.processed_successfully || 0} payments processed successfully.`,
        });
        resetForm();
        onUploadSuccess?.();
        onClose();
        return;
      }

      if (result.status === 'uploaded' && result.response) {
        toast.success('Payment file uploaded successfully', {
          id: 'upload-progress',
          description: 'Automatic processing is off. The file was stored and was not processed.',
        });
        resetForm();
        onUploadSuccess?.();
        onClose();
        return;
      }

      if (result.status === 'processing_failed') {
        const message =
          result.error?.message ??
          'The file was uploaded, but processing failed. It is not ready for review.';
        setStoredFile(selectedFile);
        setSubmitNotice({
          kind: 'processing_failed',
          message,
          fileId: result.response?.file.file_id ?? null,
        });
        toast.error('Processing failed', {
          id: 'upload-progress',
          description:
            'The file was uploaded, but processing failed. It is not ready for review. Open it in Payment Files.',
        });
        return;
      }

      const uploadMessage = result.error?.message ?? 'Upload failed';
      setError(new Error(uploadMessage));
      setSubmitNotice({ kind: 'upload_failed', message: uploadMessage, fileId: null });
      toast.error('Failed to submit file', {
        id: 'upload-progress',
        description: uploadMessage,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(new Error(errorMessage));
      setSubmitNotice({ kind: 'upload_failed', message: errorMessage, fileId: null });
      toast.error('Failed to submit file', {
        id: 'upload-progress',
        description: errorMessage,
      });
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setDescription('');
    setError(null);
    setValidationResponse(null);
    setStage('upload');
    setSubmitNotice(null);
    setStoredFile(null);
    resetUpload();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (!isValidating && !isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setValidationResponse(null);
    setSubmitNotice(null);
    setStoredFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Modal title and description
  const getModalTitle = () => {
    if (stage === 'upload') return 'Upload Payment File';
    return isSuccess ? 'Validation Successful' : 'Validation Failed';
  };

  const getModalDescription = () => {
    if (stage === 'upload') {
      return 'Upload a CSV file containing payment details. The file will be validated against the payment schema before processing.';
    }
    return isSuccess
      ? `${selectedFile?.name} has been validated. Review the data below and submit to proceed.`
      : `${selectedFile?.name} has validation errors. Please fix the issues and try again.`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={
          stage === 'preview' ? 'sm:max-w-[90vw] max-h-[85vh] flex flex-col' : 'sm:max-w-[600px]'
        }
      >
        {/* Header */}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {stage === 'preview' &&
              (isSuccess ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              ))}
            {getModalTitle()}
          </DialogTitle>
          <DialogDescription>{getModalDescription()}</DialogDescription>
          {actingOnBehalfClientName && (
            <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 w-fit dark:text-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30">
              Acting on behalf of {actingOnBehalfClientName}
            </p>
          )}
        </DialogHeader>

        {/* Content */}
        {stage === 'upload' && (
          <UploadStage
            selectedFile={selectedFile}
            description={description}
            isValidating={isValidating}
            error={error}
            dragActive={dragActive}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            onDrag={handleDrag}
            onDrop={handleDrop}
            onDescriptionChange={setDescription}
            onClearFile={handleClearFile}
          />
        )}

        {stage === 'preview' && submitNotice ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-red-800">
              <p className="font-medium">
                {submitNotice.kind === 'processing_failed'
                  ? 'Upload succeeded, but processing failed'
                  : 'Upload failed'}
              </p>
              <p className="mt-1">{submitNotice.message}</p>
              {submitNotice.kind === 'processing_failed' ? (
                <p className="mt-2">
                  The file is not ready for review.{' '}
                  <Link
                    to={
                      submitNotice.fileId
                        ? `/payment-files/${submitNotice.fileId}`
                        : '/payment-files'
                    }
                    className="underline underline-offset-4"
                  >
                    Open it in Payment Files
                  </Link>
                  .
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {stage === 'preview' && validationResponse && (
          <PreviewStage
            validationResponse={validationResponse}
            selectedFileName={selectedFile?.name || ''}
            isSuccess={isSuccess}
            validationResult={validationResult}
            previewRows={previewRows}
            columnHeaders={columnHeaders}
          />
        )}

        {/* Footer */}
        <DialogFooter>
          {stage === 'upload' ? (
            <>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isValidating}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleValidate}
                disabled={!selectedFile || isValidating}
              >
                {isValidating ? 'Validating...' : 'Validate & Preview'}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStage('upload')}
                disabled={isSubmitting}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!isSuccess || isSubmitting || storedFile === selectedFile}
              >
                {isUploading ? 'Uploading...' : isProcessing ? 'Processing...' : 'Submit & Process'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
